import { apiSwaggerClientSide } from '@/shared/api';
import { ButtonShadcn } from '@/components/ui/button-shadcn';
import { Skeleton } from '@/components/ui/skeleton';
import { KeycloakUser, Sample } from '@/sharedTypes/Api';
import { ChevronLeft, ChevronRight, Music, Users } from 'lucide-react';
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';

interface IUserDetails {
  id?: string | null;
}

interface UserGroup {
  _id?: string;
  name?: string;
}

const UserDetail = ({ id }: IUserDetails) => {
  const PAGE_SIZE = 10;
  const [isLoading, setIsLoading] = useState(true);
  const [isSamplesLoading, setIsSamplesLoading] = useState(false);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [samples, setSamples] = useState<string[]>([]);
  const [samplesPage, setSamplesPage] = useState(1);
  const [totalSamples, setTotalSamples] = useState(0);
  const [currentUser, setCurrentUser] = useState<KeycloakUser | undefined>(undefined);
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language === 'en' ? 'en' : 'ru';

  // Fetch user details and first page of samples
  useEffect(() => {
    if (!id) return;

    const fetchUserDetails = async () => {
      setIsLoading(true);
      try {
        const res = await apiSwaggerClientSide.admin.usersVisibilityDetail(id, {
          page: 1,
          pageSize: PAGE_SIZE,
        });

        if (res.data?.success && res.data?.data) {
          const data = res.data.data;

          if (data.summary?.userGroups) {
            setUserGroups(data.summary.userGroups);
          }

          const usersPrivateSamples = data.samples || [];
          const sampleTitles = usersPrivateSamples.map((s: Sample) => {
            return s.metadata?.title?.[currentLang] || s.metadata?.title?.en || "Untitled Sample";
          });

          setSamples(sampleTitles);
          setTotalSamples(data.pagination?.total || 0);
          setCurrentUser(data.user);
          setSamplesPage(1);
        }
      } catch (error) {
        console.error("[UserDetail] Error fetching user details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserDetails();
  }, [id, currentLang]);

  // Fetch samples when page changes (skip initial load)
  useEffect(() => {
    if (samplesPage === 1 || !id) return;

    const fetchSamplesPage = async () => {
      setIsSamplesLoading(true);
      try {
        const res = await apiSwaggerClientSide.admin.usersVisibilityDetail(id, {
          page: samplesPage,
          pageSize: PAGE_SIZE,
        });

        if (res.data?.success && res.data?.data) {
          const usersPrivateSamples = res.data.data.samples || [];
          const sampleTitles = usersPrivateSamples.map((s: Sample) => {
            return s.metadata?.title?.[currentLang] || s.metadata?.title?.en || "Untitled Sample";
          });
          setSamples(sampleTitles);
        }
      } catch (error) {
        console.error("[UserDetail] Error fetching samples page:", error);
      } finally {
        setIsSamplesLoading(false);
      }
    };

    fetchSamplesPage();
  }, [samplesPage, id, currentLang]);

  if(!id) {
    return <div className="text-gray-500 p-4">{t('adminCatalogue.userDetail.noUserIdProvided')}</div>
  }

  // Pagination calculations
  const totalPages = Math.ceil(totalSamples / PAGE_SIZE);

  // Skeleton for loading state
  const renderSkeleton = () => (
    <div className='flex flex-col h-full gap-5' data-testid="user-details-view-skeleton">
      {/* Header skeleton */}
      <div className="flex items-center gap-3 pb-3 border-b">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="flex flex-col gap-1">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>

      <div className='flex gap-6 h-full w-full min-h-0'>
        {/* Groups skeleton */}
        <div className='flex flex-col gap-3 w-2/5'>
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-5 w-28" />
          </div>
          <div className='flex flex-wrap gap-2'>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={`group-skeleton-${i}`} className="h-7 w-24 rounded-full" />
            ))}
          </div>
        </div>

        {/* Samples skeleton */}
        <div className='flex flex-col gap-3 w-3/5'>
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-5 w-32" />
          </div>
          <div className='border rounded-lg overflow-hidden flex-1'>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={`sample-skeleton-${i}`} className="px-4 py-3 border-b last:border-b-0">
                <Skeleton className="h-4 w-4/5" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return renderSkeleton();
  }

  return (
    <div className='flex flex-col h-full gap-5' data-testid="user-details-view">
      {/* User header */}
      <div className="flex items-center gap-3 pb-3 border-b" data-testid="user-detail-header">
        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-semibold text-lg">
          {currentUser?.username?.charAt(0).toUpperCase() || '?'}
        </div>
        <div className="flex flex-col">
          <span className="font-semibold text-gray-900">{currentUser?.username || 'Unknown user'}</span>
          {currentUser?.email && (
            <span className="text-sm text-gray-500">{currentUser.email}</span>
          )}
        </div>
      </div>

      <div className='flex gap-6 h-full w-full min-h-0'>
        {/* Groups column */}
        <div className='flex flex-col gap-3 w-2/5'>
          <div className="flex items-center gap-2 text-gray-700" data-testid="user-groups-header">
            <Users className="h-5 w-5" />
            <span className="font-semibold">{t('adminCatalogue.userDetail.groupsCount', { count: userGroups.length })}</span>
          </div>

          {userGroups.length > 0 ? (
            <div className='flex flex-wrap gap-2 overflow-y-auto'>
              {userGroups.map(group => (
                <span
                  key={group._id}
                  className="px-3 py-1.5 text-sm font-medium rounded-full bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
                >
                  {group.name}
                </span>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-24 border rounded-lg bg-gray-50">
              <span className="text-gray-500 text-sm">{t('adminCatalogue.userDetail.noGroupsAssigned')}</span>
            </div>
          )}
        </div>

        {/* Samples column */}
        <div className='flex flex-col gap-3 w-3/5 min-h-0'>
          <div className="flex items-center gap-2 text-gray-700" data-testid="user-detail-private-samples-header">
            <Music className="h-5 w-5" />
            <span className="font-semibold">{t('adminCatalogue.userDetail.privateMusicSheets', { count: totalSamples })}</span>
          </div>

          {totalSamples > 0 ? (
            <>
              <div className='border rounded-lg overflow-hidden flex-1 overflow-y-auto'>
                {samples.map((sample: string, index: number) => (
                  <div
                    key={`${sample}-${index}`}
                    className="px-4 py-3 border-b last:border-b-0 hover:bg-gray-50 transition-colors flex items-center gap-3"
                    data-testid={`user-detail-private-sample-item-${index}`}
                  >
                    <span className="w-6 h-6 rounded bg-green-100 text-green-700 flex items-center justify-center text-xs font-medium">
                      {(samplesPage - 1) * PAGE_SIZE + index + 1}
                    </span>
                    <span className="text-sm text-gray-700 truncate">{sample}</span>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-2">
                  <span className="text-sm text-gray-600">
                    {t('adminCatalogue.common.showing', { from: (samplesPage - 1) * PAGE_SIZE + 1, to: Math.min(samplesPage * PAGE_SIZE, totalSamples), total: totalSamples })}
                  </span>
                  <div className="flex items-center gap-2">
                    <ButtonShadcn
                      variant="outline"
                      size="sm"
                      onClick={() => setSamplesPage(p => p - 1)}
                      disabled={samplesPage <= 1 || isSamplesLoading}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </ButtonShadcn>
                    <span className="text-sm text-gray-600 min-w-[80px] text-center">
                      {isSamplesLoading ? '...' : `${samplesPage} / ${totalPages}`}
                    </span>
                    <ButtonShadcn
                      variant="outline"
                      size="sm"
                      onClick={() => setSamplesPage(p => p + 1)}
                      disabled={samplesPage >= totalPages || isSamplesLoading}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </ButtonShadcn>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-24 border rounded-lg bg-gray-50">
              <span className="text-gray-500 text-sm">{t('adminCatalogue.userDetail.noPrivateMusicSheets')}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default UserDetail